import { TutorialConfig, TutorialStep, TutorialTask } from '../types';

const FREECAD_TASKS: TutorialTask[] = [
  {
    id: 'task-1',
    name: 'apply-symmetry-constraint-to-stadium-no-axes',
    type: 'success',
    description: 'Introduction to FreeCAD evaluation',
    localDataPath: '/onboarding_messages/messages_1.json'
  },
  {
    id: 'task-2',
    name: 'create-cube-and-shell',
    type: 'failure',
    description: 'Creating a cube example',
    localDataPath: '/onboarding_messages/messages_2.json'
  },
  {
    id: 'task-3',
    name: 'complex-task-example',
    type: 'failure',
    description: 'Complex task evaluation',
    localDataPath: '/onboarding_messages/messages_3.json'
  },
  {
    id: 'task-4',
    name: 'unclear-instructions',
    type: 'bad_task',
    description: 'Example of ambiguous instructions',
    localDataPath: '/onboarding_messages/messages_4.json'
  },
  {
    id: 'task-5',
    name: 'final-practice',
    type: 'bad_task',
    description: 'Final practice task',
    localDataPath: '/onboarding_messages/messages_5.json'
  }
];

const FREECAD_STEPS: TutorialStep[] = [
  // Task 1: Introduction and controls
  {
    id: "welcome",
    title: "Welcome to FreeCAD Evaluation!",
    content:
      "This tutorial will show you how to evaluate FreeCAD model runs. We'll go through 5 example tasks.",
    targetSelector: ".App-header h1",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-description",
    title: "Task Description",
    content:
      "This shows the instructions given to the AI model to complete inside of the FreeCAD program. Read this carefully to understand what the model was asked to do.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "skip-to-frame-2",
    title: "Skip to Frame 2",
    content:
      "Click the right arrow button or press the RIGHT arrow key to go to the next frame.",
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "current-action",
    title: "Current Action",
    content:
      "This section describes what action the AI model is performing in the current frame. It helps you understand what the model is trying to do.",
    targetSelector: ".sidebar-section:nth-child(3)",
    action: "next",
    position: "right",
  },
  {
    id: "main-click",
    title: "Main Click Indicator",
    content:
      "The circle with ripple effect shows where the model clicked on the screen in this frame. This helps you track the model's interactions.",
    targetSelector: ".click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "next-click",
    title: "Next Click Preview",
    content:
      "The faded circle shows where the model will click in the next frame. This helps you anticipate the model's next action.",
    targetSelector: ".next-click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "go-to-frame-3",
    title: "Go to Next Frame",
    content:
      'Click the right arrow button to advance to frame 3. Notice how the previous "next click" becomes the current click.',
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "click-transition",
    title: "Click Transition",
    content:
      'Notice that the model has now clicked where the "next click" indicator was in the previous frame. This shows the continuity of actions.',
    targetSelector: ".click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "seek-backward",
    title: "Previous Frame",
    content:
      "Click the left arrow button or press the LEFT arrow key to go back to the previous frame.",
    targetSelector: ".nav-button:first-child",
    action: "seek",
    position: "top",
  },
  {
    id: "play",
    title: "Play the Video",
    content: "Click the play button or press SPACE to start playing the video.",
    targetSelector: ".play-pause-btn",
    action: "play",
    position: "top",
  },
  {
    id: "pause",
    title: "Pause the Video",
    content:
      "Now click the pause button (same button) or press SPACE again to pause the video.",
    targetSelector: ".play-pause-btn",
    action: "pause",
    position: "top",
  },
  {
    id: "seek-timeline",
    title: "Navigate Through Timeline",
    content:
      "You can also drag the timeline slider to jump to any point in the video. Try it now, then click Next when ready.",
    targetSelector: ".video-timeline",
    action: "next",
    position: "top",
  },
  {
    id: "speed",
    title: "Adjust Playback Speed",
    content:
      "Click here to change the video playback speed (1x, 2x, 4x). You can also use the UP/DOWN arrow keys to adjust speed.",
    targetSelector: ".speed-selector",
    action: "speed",
    position: "top",
  },
  {
    id: "finalframe",
    title: "Important: Final Frame",
    content:
      "You can only evaluate the task once you've reached the final frame. Watch as we skip to the end.",
    action: "next",
  },
  {
    id: "task-success",
    title: "Task Completed Successfully",
    content: "The model successfully completed the task as requested.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "left",
  },
  {
    id: "evaluate",
    title: "Mark as Success",
    content:
      'Since the task was completed successfully, click the "Success" button.',
    targetSelector: ".evaluation-btn.success",
    action: "evaluate",
    position: "top",
  },
  {
    id: "progress-saved",
    title: "Your Progress is Saved",
    content:
      "You can exit anytime - your evaluations are saved automatically. Feel free to work at your own pace.",
    action: "next",
  },
  {
    id: "task-1-complete",
    title: "Example 1 Complete!",
    content:
      'Great job! Now click the "Next task" button to continue to the next example.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 2: Pass example
  {
    id: "task-2-intro",
    title: "Example 2: Creating a Cube",
    content:
      "In this task, the AI was asked to create a 50x50x50 mm cube and shell it. Watch the task and evaluate how it performed.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-2-evaluate",
    title: "Evaluate the Task",
    content:
      "Remember: You must watch the complete video to the end before you can evaluate. Once you've seen the final result, decide whether the task was completed successfully.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
  },
  {
    id: "task-2-explanation",
    title: "Why This Failed",
    content:
      "Correct! The model fails in frame 3 by making a new document, rather than using the existing one.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "left",
  },
  {
    id: "task-2-complete",
    title: "Example 2 Complete",
    content: 'Good! Click "Next task" to see another example.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 3: Another fail example
  {
    id: "task-3-intro",
    title: "Example 3",
    content:
      "Read the task description. Carefully watch the video and evaluate the result.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-3-evaluate",
    title: "Evaluate the Task",
    content:
      "Make sure you've watched to the end of the video. Review the task completion and decide on your evaluation.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
  },
  {
    id: "task-3-explanation",
    title: "Why This Failed",
    content:
      "Correct! The model fails because in frame 21, it unnecessarily modifies the Z dimension, which changes the height of the additive loft.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "left",
  },
  {
    id: "task-3-complete",
    title: "Example 3 Complete",
    content: 'Well done! Click "Next task" for another example.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 4: Bad task example
  {
    id: "task-4-intro",
    title: "Example 4: Unclear Instructions",
    content:
      "Sometimes task descriptions can be ambiguous or unclear. Let's see an example.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-4-ambiguous",
    title: "Ambiguous Task",
    content:
      'This task description is unclear and could be interpreted in multiple ways. This is a good candidate for "Bad Task".',
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-4-bad-task",
    title: "Use Bad Task Button",
    content:
      'When instructions are unclear or have multiple valid interpretations, click "Bad Task".',
    targetSelector: ".evaluation-btn.bad-task",
    action: "evaluate",
    position: "top",
  },
  {
    id: "task-4-complete",
    title: "Example 4 Complete",
    content: 'Good job! Click "Next task" for the final example.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 5: Final review
  {
    id: "task-5-intro",
    title: "Example 5: Final Practice",
    content:
      "This is your final practice task. Evaluate it based on what you've learned.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-5-evaluate",
    title: "Make Your Evaluation",
    content:
      "Watch the complete video first, then decide: Pass, Fail, or Bad Task?",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
  },
  {
    id: "tutorial-complete",
    title: "Tutorial Complete!",
    content:
      "Congratulations! You've completed the onboarding tutorial. Click below to start evaluating real tasks.",
    action: "next",
    position: "bottom",
  },
];

export const freecadTutorialConfig: TutorialConfig = {
  software: 'freecad',
  displayName: 'FreeCAD',
  totalTasks: 5,
  tasks: FREECAD_TASKS,
  steps: FREECAD_STEPS,
  completionKey: 'onboarding_v2_completed',
  dataSource: 'local',
  fetchTaskData: async (task: TutorialTask) => {
    if (task.localDataPath) {
      console.log(`Fetching FreeCAD tutorial data for task ${task.id} from ${task.localDataPath}`);
      const response = await fetch(task.localDataPath);
      const messages = await response.json();
      console.log(`Fetched ${messages.length} messages for task ${task.id}`);
      
      // Extract task description from messages
      let taskPrompt = "Loading task...";
      if (messages && messages.length > 0 && messages[0]?.content) {
        // Content might be an array or string, handle both cases
        const firstMessageContent = messages[0].content;
        let textContent = '';
        
        if (typeof firstMessageContent === 'string') {
          textContent = firstMessageContent;
        } else if (Array.isArray(firstMessageContent)) {
          // Look for text content in the array
          const textItem = firstMessageContent.find(item => item.type === 'text');
          if (textItem?.text) {
            textContent = textItem.text;
          }
        }
        
        // Look for the task after "Navigate a computer using computer_use to"
        const taskMatch = textContent.match(/Navigate a computer using computer_use to\s*\n(.+?)(?:\n|$)/);
        if (taskMatch && taskMatch[1]) {
          taskPrompt = taskMatch[1].trim();
        } else {
          // Fallback: try to extract any text after the system capability section
          const fallbackMatch = textContent.match(/Navigate a computer using computer_use to\s*\n([\s\S]+?)$/);
          if (fallbackMatch && fallbackMatch[1]) {
            taskPrompt = fallbackMatch[1].trim();
          }
        }
      }
      
      const result = {
        messages,
        problem: {
          name: task.name,
          spec: {
            prompt: taskPrompt
          }
        }
      };
      console.log(`FreeCAD fetchTaskData returning:`, result);
      return result;
    }
    return null;
  }
};